import React, { createContext, FC, useEffect, useState, useMemo } from 'react';
import { Avatar, Button, Spin, Typography } from 'antd';
import { UserOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { selectBookingAppointmentData, selectBookingData } from '../../redux/slices/booking';
import { useGetAvailabilitiesBySpecialistQuery } from '../../services/availabilities';
import { AppointmentData, AppointmentType, BookingTime } from '../../model';
import { MAIN_COLOR } from '../../styles/color';
import Specialist from '../../model/Specialist';
import SpecialistProfile from '../SpecialistProfile';
import SelectBookingTimeModal from '../SelectBookingTimeModal';
import { DATE_WITH_DASH, MONTH } from '../../constants/time-formats';
import { DEVICES, QUERIES } from '../../styles/breakpoint';
import analytics from '../../infra/analytics';
import { getSpecialistStepConfig } from '../../config/privateBooking';

type BookingTimeContextType = {
  bookingTime: BookingTime;
  // eslint-disable-next-line no-unused-vars
  updateBookingTime: (data: BookingTime) => void;
};

type Props = {
  specialist: Specialist;
  openCalendar: boolean;
};

const initBookingTimeContext = {
  bookingTime: {
    month: moment().format(MONTH),
  },
  updateBookingTime: () => {},
};

export const BookingTimeContext = createContext<BookingTimeContextType>(
  initBookingTimeContext,
);

const PersonalInfoTile = styled.div`
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
  @media screen and (min-width: 1100px){
    gap: 24px;
    justify-content: center;
  }
`;

const AvatarWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BookingInfoTile = styled.div`
  background-color: #f5f5f5;
  border-radius: 8px;
  text-align: left;
  display: flex;
  flex-direction: column;
  padding: 8px;
  margin-top: 12px;
  font-size: 12px;
  ul {
    list-style-position: inside;
  }
  ${DEVICES.desktop} {
    margin-top: 24px;
    padding: 16px;
    font-size: 16px;
  }
`;

const SpecialistCard: FC<Props> = ({ specialist, openCalendar }) => {
  const { avatarLink, profileLink, id, appointmentLength } = specialist;
  const isMobile = useMediaQuery({ query: QUERIES.mobile });
  const isDesktop = useMediaQuery({ query: QUERIES.desktop });
  const { type } = useSelector(selectBookingAppointmentData) as AppointmentData;
  const appointmentType = useMemo(() => type ?? AppointmentType.NEW, [type]);
  const [isBookingTimeModalOpen, setIsBookingTimeModalOpen] = useState(false);
  const [bookingTime, setBookingTime] = useState(
    initBookingTimeContext.bookingTime,
  );
  const initialApptFee = specialist.fee?.initial;
  // const isBulkBill = initialApptFee?.outOfPocket === '0' && initialApptFee?.total === '0' && initialApptFee?.medicareRebate === '0';
  const { metadata } = useSelector(selectBookingData);
  const { centreId } = metadata || {};
  const { requireCentreSelection = false } = getSpecialistStepConfig() || {};
  const disbaleBooking = requireCentreSelection && !centreId;
  const { isLoading: isLoadingAvailableTimeList, data: availableTimeList } = useGetAvailabilitiesBySpecialistQuery({
    specialistId: id,
    appointmentType,
    aptLength: appointmentLength?.split('-')[0],
    locationId: centreId,
  });

  const nextAvailableTime = useMemo(() => {
    if (!availableTimeList || availableTimeList.length === 0) return null;

    const today = moment().format(DATE_WITH_DASH);

    return availableTimeList.find(
      (availableTime) => availableTime.date >= today,
    );
  }, [availableTimeList]);

  const { search, pathname } = useLocation();

  useEffect(() => {
    if (openCalendar) {
      setIsBookingTimeModalOpen(true);

      // Track calendar open even
      analytics.trackVpcCalendarOpen({
        page_path: pathname,
      });
    }
  }, [openCalendar, pathname]);

  useEffect(() => {
    if (nextAvailableTime && nextAvailableTime.date) {
      setBookingTime({
        month: moment(nextAvailableTime.date).format(MONTH),
      });
    }
  }, [nextAvailableTime]);

  const searchParams = new URLSearchParams(search);
  const code = searchParams.get('code');

  const handleStartBooking = () => {
    analytics.trackVpcStartBooking({
      button_text: 'Book a session',
      page_path: pathname,
    });

    setIsBookingTimeModalOpen(true);

    // Track calendar open even
    analytics.trackVpcCalendarOpen({
      page_path: pathname,
    });
  };

  return (
    <BookingTimeContext.Provider
      value={{ bookingTime, updateBookingTime: setBookingTime }}
    >
      <div>
        <PersonalInfoTile>
          <AvatarWrapper>
            <Avatar
              src={avatarLink}
              icon={<UserOutlined />}
              // size={isMobile ? 80 : 150}
              size={{ xs: 90, sm: 150, md: 60, lg: 120, xl: 150, xxl: 150 }}
            />
          </AvatarWrapper>

          <div>
            <SpecialistProfile specialist={specialist} />
            {isLoadingAvailableTimeList ? (
              <Spin tip="Loading Next Available Time..." />
            ) : (
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  padding: '8px',
                  borderRadius: '8px',
                }}
              >
                <Typography.Text
                  style={{
                    // width: isMobile ? '230px' : '280px',
                    minWidth: isDesktop ? '280px' : undefined,
                    fontWeight: 300,
                    fontSize: isMobile ? '12px' : '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <CalendarOutlined />
                  {nextAvailableTime ? (
                    <>
                      Next Available:
                      {' '}
                      {moment(nextAvailableTime.date).format('DD/MM/YYYY')}
                      {' '}
                      {nextAvailableTime.availableTimeslots[0].start}
                    </>
                  ) : (
                    <>No times available</>
                  )}
                </Typography.Text>
              </div>
            )}
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button
                type="primary"
                style={{
                  // marginRight: isMobile ? '45px' : '32px',
                  height: isMobile ? '32px' : '48px',
                  fontSize: isMobile ? '12px' : '18px',
                }}
                size={isMobile ? 'small' : 'middle'}
                onClick={handleStartBooking}
                disabled={disbaleBooking}
              >
                Book a session
              </Button>
              <Button
                type="text"
                style={{
                  color: MAIN_COLOR,
                  padding: 0,
                  fontSize: isMobile ? '12px' : '18px',
                  fontWeight: 600,
                }}
                size={isMobile ? 'small' : 'middle'}
                onClick={() => window.open(profileLink, '_blank')}
              >
                View Profile
              </Button>
            </div>
          </div>

        </PersonalInfoTile>
        {code !== 'X7mP92qL' && (
          <BookingInfoTile>
            <ul>
              <li>
                <Typography.Text style={{ fontWeight: 300 }}>
                  Standard Fee:
                  {` $${initialApptFee?.total}; Medicare rebate: $${initialApptFee?.medicareRebate}; `}
                  <strong>
                    {`Out-of-pocket: $${initialApptFee?.outOfPocket}`}
                  </strong>
                </Typography.Text>
              </li>
              <li>
                <Typography.Text style={{ fontWeight: 300 }}>
                  MyHealth Patients, Medibank and ahm Members Offer: $193.95; Medicare rebate: $98.95,
                  {' '}
                  <strong>Out-of-pocket: $95.00</strong>
                </Typography.Text>
              </li>
              <li>
                <Typography.Text style={{ fontWeight: 300 }}>
                  Clients who commenced counselling prior to 4 August 2025 will retain their current pricing.
                </Typography.Text>
              </li>
            </ul>
          </BookingInfoTile>
        )}
      </div>

      <SelectBookingTimeModal
        specialist={specialist}
        isBookingTimeModalOpen={isBookingTimeModalOpen}
        setIsBookingTimeModalOpen={setIsBookingTimeModalOpen}
      />
      {/* <Modal visible={isFeeAlertModalOpen} onOk={handleOpenBookingModal} onCancel={() => setIsFeeAlertModalOpen(false)} okText="Continue with booking"> */}
      {/*  {isBulkBill && ( */}
      {/*    <Typography.Paragraph>For this appointment, with a valid Medicare Card and a GP referral, the appointment will be bulk billed by Medicare.</Typography.Paragraph> */}
      {/*  )} */}
      {/*  {(!isBulkBill && initialApptFee) && ( */}
      {/*    <> */}
      {/*      <Typography.Paragraph>{`For this appointment, with a valid Medicare Card and a GP referral, the estimated out-of-pocket cost is $${initialApptFee?.outOfPocket}.`}</Typography.Paragraph> */}
      {/*      <Typography.Paragraph>{`You will be required to pay an upfront full fee of up to $${initialApptFee?.total}, of which up to $${initialApptFee?.medicareRebate} will be claimed back from Medicare within 48 hours.`}</Typography.Paragraph> */}
      {/*    </> */}
      {/*  )} */}
      {/*  {(!isBulkBill && !initialApptFee) && (<Typography.Paragraph>Your consult will be approximately $100 for an initial consult and $60 for a follow-up appointment.</Typography.Paragraph>)} */}
      {/* </Modal> */}
    </BookingTimeContext.Provider>
  );
};

export default SpecialistCard;
